import { FC, memo, useEffect, useRef } from "react";
import { Pressable, Animated, StyleSheet, View as RNView } from "react-native";
import { Text } from "@src/components";
import { useUnistyles } from "react-native-unistyles";

type StillWatchingViewProps = {
  /** Seconds remaining before the stream is automatically stopped */
  autoStopSeconds: number;
  onContinue: () => void;
  onStop: () => void;
};

/**
 * Overlay asking the user if they're still watching before the server-side
 * stream timeout expires. Shown ~15s before the Lambda aborts the connection.
 */
export const StillWatchingView: FC<StillWatchingViewProps> = memo(({ autoStopSeconds, onContinue, onStop }) => {
  const { theme } = useUnistyles();

  // Shrinking progress bar to visualise the remaining time
  const progressAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: 0,
      duration: autoStopSeconds * 1000,
      useNativeDriver: false,
    }).start();
  }, [autoStopSeconds, progressAnim]);

  return (
    <RNView style={styles.container}>
      <RNView
        style={[
          styles.content,
          {
            backgroundColor: "rgba(0,0,0,0.85)",
            borderRadius: theme.spacing.lg,
            padding: theme.spacing["2xl"],
            gap: theme.spacing.lg,
          },
        ]}
      >
        <Text textStyle="lg/bold" style={styles.title}>
          Still watching?
        </Text>
        <Text textStyle="md/regular" style={styles.subtitle}>
          The stream will stop automatically to save bandwidth.
        </Text>

        {/* Progress bar */}
        <RNView style={[styles.progressTrack, { backgroundColor: theme.colors.grey[700] }]}>
          <Animated.View
            style={[
              styles.progressFill,
              {
                backgroundColor: theme.colors.white.default,
                width: progressAnim.interpolate({ inputRange: [0, 1], outputRange: ["0%", "100%"] }),
              },
            ]}
          />
        </RNView>

        <RNView style={[styles.buttons, { gap: theme.spacing.lg }]}>
          <Pressable
            onPress={onStop}
            style={[
              styles.button,
              {
                borderWidth: 1,
                borderColor: theme.colors.grey[500],
                borderRadius: theme.spacing.md,
              },
            ]}
          >
            <Text textStyle="md/semibold" style={{ color: theme.colors.grey[300] }} numberOfLines={1}>
              Stop
            </Text>
          </Pressable>

          <Pressable
            onPress={onContinue}
            style={[
              styles.button,
              {
                backgroundColor: theme.colors.white.default,
                borderRadius: theme.spacing.md,
              },
            ]}
          >
            <Text textStyle="md/semibold" style={{ color: "black" }} numberOfLines={1}>
              Keep watching
            </Text>
          </Pressable>
        </RNView>
      </RNView>
    </RNView>
  );
});

StillWatchingView.displayName = "StillWatchingView";

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 10,
  },
  content: {
    width: "80%",
    maxWidth: 400,
    alignItems: "center",
  },
  title: {
    color: "white",
    textAlign: "center",
  },
  subtitle: {
    color: "#aaa",
    textAlign: "center",
  },
  progressTrack: {
    width: "100%",
    height: 3,
    borderRadius: 2,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 2,
  },
  buttons: {
    flexDirection: "row",
    width: "100%",
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    alignItems: "center",
    justifyContent: "center",
  },
});
